import { useMemo } from 'react'
import {
  createPatientInputValidations,
  getAllValidationErrors,
  isFormValid,
  shouldShowValidationError,
  validateAttendants,
  validateAlternativeMobiles,
} from '../utils/patientValidationUtils'

export const usePatientValidation = (
  patientDetails,
  makeAnonymousPatient,
  hideInputValidations = false
) => {
  const validations = useMemo(
    () => createPatientInputValidations(patientDetails, makeAnonymousPatient, hideInputValidations),
    [patientDetails, makeAnonymousPatient, hideInputValidations]
  )

  const validationErrors = useMemo(
    () => getAllValidationErrors(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient]
  )

  const attendantValidations = useMemo(
    () => validateAttendants(patientDetails?.attendants),
    [patientDetails?.attendants]
  )

  const alternativeMobileValidations = useMemo(
    () => validateAlternativeMobiles(patientDetails?.altMobiles),
    [patientDetails?.altMobiles]
  )

  const isValid = useMemo(
    () => isFormValid(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient]
  )

  const hasErrors = useMemo(
    () => validationErrors.length > 0 || attendantValidations.length > 0 || alternativeMobileValidations.length > 0,
    [validationErrors, attendantValidations, alternativeMobileValidations]
  )

  const getFieldValidation = (fieldName) => {
    return {
      isValid: validations[fieldName] !== false,
      showError: shouldShowValidationError(validations[fieldName], hideInputValidations),
      errorMessage: validations[fieldName] === false ? `${fieldName} is required` : null,
    }
  }

  const getAttendantValidation = (index) => {
    const validation = attendantValidations.find(v => v.index === index)
    return {
      isValid: !validation,
      showError: !!validation,
      errorMessage: validation?.message || null,
    }
  }

  const getAlternativeMobileValidation = (index) => {
    const validation = alternativeMobileValidations.find(v => v.index === index)
    return {
      isValid: !validation,
      showError: !!validation,
      errorMessage: validation?.message || null,
    }
  }

  const getAllErrors = () => {
    return {
      fieldErrors: validationErrors,
      attendantErrors: attendantValidations,
      alternativeMobileErrors: alternativeMobileValidations,
    }
  }

  const getErrorSummary = () => {
    const allErrors = getAllErrors()
    const totalErrors = allErrors.fieldErrors.length + allErrors.attendantErrors.length + allErrors.alternativeMobileErrors.length

    return {
      totalErrors,
      hasFieldErrors: allErrors.fieldErrors.length > 0,
      hasAttendantErrors: allErrors.attendantErrors.length > 0,
      hasAlternativeMobileErrors: allErrors.alternativeMobileErrors.length > 0,
      summary: `${totalErrors} validation error${totalErrors !== 1 ? 's' : ''} found`,
    }
  }

  return {
    validations,
    validationErrors,
    attendantValidations,
    alternativeMobileValidations,
    isValid,
    hasErrors,
    getFieldValidation,
    getAttendantValidation,
    getAlternativeMobileValidation,
    getAllErrors,
    getErrorSummary,
  }
}